import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router';
import omit from 'lodash/omit';
import last from 'lodash/last';
import { Button } from 'reactstrap';

// Types
import { Illustrations } from 'Wpds/components/illustration';
import { WorkspaceType } from 'Shared/constants/types';

// Components
import WpToastManager from 'Wpds/components/toast/WpToastManager';
import TextField from 'Shared/components/TextField/TextField';
import SimpleAddWorkspaceWrapper from '../SimpleAddWorkspaceWrapper/SimpleAddWorkspaceWrapper';

// Helpers
import { isEmptyString, defaultTeammateMessage } from 'Shared/helpers';

// Actions
import { bulkCreateWorkspaces } from 'Shared/actions/workspaces';

// Styles
import './SimpleAddMultipleTemmatesModal.scss';
import { WpIcon } from 'Wpds/components/icon';

type Props = {
  onClose: Function;
};

const initialValues = { 0: '', 1: '', 2: '' };

function SimpleAddMultipleTemmatesModal({ onClose }: Props) {
  const dispatch = useDispatch();
  const history = useHistory();
  const [formValues, setFormValues] = useState(initialValues);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async () => {
    try {
      setIsSubmitting(true);
      const teammatesNames = Object.values(formValues).filter(value => !isEmptyString(value));
      const workspacesParams = teammatesNames.map(teammateName => ({
        name: teammateName,
        inviteeEmails: [null],
        personalMessage: defaultTeammateMessage,
      }));
      const firstCreatedWorkspace = await dispatch(bulkCreateWorkspaces({ workspacesParams }));
      if (firstCreatedWorkspace?.path) history.push(firstCreatedWorkspace?.path);
    } catch (e) {
      setIsSubmitting(false);
      WpToastManager.danger('Something went wrong!');
    }
    return null;
  };

  const handleTextInputChange = event => {
    const key = parseInt(event.target.name, 10);
    setFormValues({ ...formValues, [key]: event.target.value });
  };

  const handleRemoveItem = key => setFormValues(omit(formValues, [key]));

  const handleNewItemAddClick = () => {
    const lastKey = last(Object.keys(formValues));
    const newKey = parseInt(lastKey, 10) + 1;
    setFormValues({ ...formValues, [newKey]: '' });
  };

  const valuesCount = Object.values(formValues).length;

  const isAllValuesEmpty = Object.values(formValues).every(value => isEmptyString(value));
  const isSubmitDisabled = isAllValuesEmpty || isSubmitting;

  return (
    <SimpleAddWorkspaceWrapper
      onClose={onClose}
      workspaceType={WorkspaceType.isTeammate}
      title="Create 1:1 Workspaces"
      description="Who do you have 1:1s with?"
      submitButtonText="Create Workspaces"
      disabledSubmit={isSubmitDisabled}
      isSubmitting={isSubmitting}
      onSubmit={handleSubmit}
      createNameSection={
        <>
          <div className="mb-3 mt-2 pr-3">
            Create a workspace for each individual you have 1:1’s with. You can invite them at a later step.
          </div>
          {/* eslint-disable-next-line react/no-unescaped-entities */}
          <div className="add-multiple-temmates-modal-label">Teammate name(s)</div>
          {Object.entries(formValues).map(formValue => (
            <div key={formValue[0]} className="d-flex">
              <TextField
                name={formValue[0]}
                type="text"
                placeholder="Enter Full Name"
                wrapperClassName="mb-2"
                value={formValue[1]}
                onChange={handleTextInputChange}
              />
              {valuesCount > 1 ? (
                <Button close className="align-self-center mb-2 ml-2" onClick={() => handleRemoveItem(formValue[0])} />
              ) : null}
            </div>
          ))}
          <button
            type="button"
            onClick={handleNewItemAddClick}
            className="add-multiple-temmates-modal-button btn btn-link text-decoration-none p-0 mb-3"
          >
            <span className="mr-1">
              <WpIcon name="plus-circle" />
            </span>
            Add another
          </button>
        </>
      }
      illustration={Illustrations.three_people_in_windows_with_purple_bg}
    />
  );
}

export default SimpleAddMultipleTemmatesModal;
